import { useEffect, useState } from "react";
import { AlertTriangle, RotateCcw, Save, Eye, EyeOff, Lock } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import { AdminTitle } from "@/admin/AdminLayout";
import { WORDING_GROUPS, canHide } from "@/lib/labels";

const tid = (k) => k.replace(/\./g, "-");

const ShowToggle = ({ item, hidden, onToggle }) => (canHide(item.key)
  ? <button type="button" data-testid={`wording-toggle-${tid(item.key)}`} onClick={onToggle} title={hidden ? "Hidden on the site — click to show" : "Shown on the site — click to hide"}
      className={`md:col-span-2 justify-self-start inline-flex items-center gap-1 text-xs font-heading px-2 py-1.5 border ${hidden ? "border-[#991b1b] text-[#991b1b] bg-[#991b1b]/10" : "border-[#2f6b3a]/60 text-[#2f6b3a]"}`}>
      {hidden ? <><EyeOff className="w-3 h-3" /> Hidden</> : <><Eye className="w-3 h-3" /> Shown</>}
    </button>
  : <span data-testid={`wording-locked-${tid(item.key)}`} title="Always shown so nobody gets locked out" className="md:col-span-2 justify-self-start inline-flex items-center gap-1 text-xs font-heading text-[#5e4a35]"><Lock className="w-3 h-3" /> Always shown</span>);

const Row = ({ item, value, onChange, hidden, onToggle }) => (
  <div className={`grid md:grid-cols-12 gap-2 md:gap-4 items-center py-3 border-b border-[#c9a227]/20 ${hidden ? "opacity-60" : ""}`}>
    <div className="md:col-span-4">
      <p className="font-heading text-sm font-semibold">{item.def}</p>
      <p className="text-xs italic text-[#5e4a35]">{item.where}</p>
    </div>
    <input data-testid={`wording-input-${tid(item.key)}`} value={value} maxLength={item.max || 80} placeholder={item.def}
      onChange={(e) => onChange(e.target.value)} className="admin-input md:col-span-5" />
    <ShowToggle item={item} hidden={hidden} onToggle={onToggle} />
    <button type="button" data-testid={`wording-reset-${tid(item.key)}`} disabled={!value} onClick={() => onChange("")} title="Back to the original wording"
      className="md:col-span-1 justify-self-start inline-flex items-center gap-1 text-xs font-heading px-2 py-1.5 border border-[#c9a227]/60 hover:bg-[#c9a227]/20 disabled:opacity-30">
      <RotateCcw className="w-3 h-3" /> Reset
    </button>
  </div>
);

const Group = ({ grp, labels, set, hidden, toggle }) => (
  <section data-testid={`wording-group-${grp.id}`} className="mb-8">
    <h3 className="font-display text-xl mb-1">{grp.title}</h3>
    <div>{grp.items.map((i) => <Row key={i.key} item={i} value={labels[i.key] || ""} onChange={(v) => set(i.key, v)} hidden={hidden.includes(i.key)} onToggle={() => toggle(i.key)} />)}</div>
  </section>
);

const CoreBanner = () => (
  <div data-testid="wording-core-warning" className="flex gap-3 items-start p-4 mb-6 border-2 border-[#991b1b] bg-[#991b1b]/[0.07]">
    <AlertTriangle className="w-5 h-5 text-[#991b1b] shrink-0 mt-0.5" />
    <p className="text-sm"><b>Important wording.</b> These are the main menu, sign-in and admin side-panel labels that visitors and you rely on to find your way around. Only the words change here. Links and how things work stay the same. Keep the labels short and clear, and use Reset if something reads wrong.</p>
  </div>
);

export default function AdminWording() {
  const { settings, refresh } = useSite();
  const [labels, setLabels] = useState({});
  const [busy, setBusy] = useState(false);
  const [hidden, setHidden] = useState([]);
  useEffect(() => { setLabels(settings.labels || {}); }, [settings.labels]);
  useEffect(() => { setHidden(settings.hidden_labels || []); }, [settings.hidden_labels]);
  const toggle = (k) => setHidden((h) => (h.includes(k) ? h.filter((x) => x !== k) : [...h, k]));
  const set = (k, v) => setLabels((l) => ({ ...l, [k]: v }));
  const save = async () => {
    setBusy(true);
    try { await api.put("/settings/labels", { labels, hidden }); await refresh(); toast.success("Wording saved"); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const everyday = WORDING_GROUPS.filter((x) => !x.core);
  const core = WORDING_GROUPS.filter((x) => x.core);

  return (
    <div data-testid="admin-wording">
      <AdminTitle eyebrow="The scribe's lexicon" title="Site Wording">
        <button data-testid="wording-save" onClick={save} disabled={busy} className="btn-illum theme-flame disabled:opacity-60"><Save className="w-4 h-4" /> {busy ? "Saving…" : "Save wording"}</button>
      </AdminTitle>
      <p className="mb-8 italic text-[#4a3a2a] max-w-3xl">Change the words on fixed headings and buttons, or use Shown / Hidden to remove one from the site. Leave a box empty to keep the original (shown in grey). Titles you already edit elsewhere, such as the site title, section names and map caption, aren't listed here.</p>
      <div className="vellum p-6 sm:p-8 mb-10">
        <p className="eyebrow mb-4">Everyday headings & buttons</p>
        {everyday.map((grp) => <Group key={grp.id} grp={grp} labels={labels} set={set} hidden={hidden} toggle={toggle} />)}
      </div>
      <div data-testid="wording-core-section" className="vellum p-6 sm:p-8 border-2 border-[#991b1b]/60">
        <p className="eyebrow mb-4">Core Navigation & Sign-in</p>
        <CoreBanner />
        {core.map((grp) => <Group key={grp.id} grp={grp} labels={labels} set={set} hidden={hidden} toggle={toggle} />)}
      </div>
      <div className="mt-8"><button data-testid="wording-save-bottom" onClick={save} disabled={busy} className="btn-illum theme-flame disabled:opacity-60"><Save className="w-4 h-4" /> {busy ? "Saving…" : "Save wording"}</button></div>
    </div>
  );
}
